import { useEffect, useState, useCallback, useRef } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import LoadingButton from '@mui/lab/LoadingButton'
import Divider from '@mui/material/Divider'
import Collapse from '@mui/material/Collapse'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import IconButton from '@mui/material/IconButton'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import CircularProgress from '@mui/material/CircularProgress'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import StopIcon from '@mui/icons-material/Stop'
import api from 'services/api'
import {
  TextArea,
  Header,
  Text,
  ExpandableTitles,
  ExpandableTranscript,
  ExpandableText,
  CopyResultsButton,
  CopyIconButton,
  formatBatchExport,
  formatVerboseResultExport,
} from './Bits'
import ModelAndThinkingConfig from './ThinkingConfig'
import { usePresetSide, PresetSelector, SchemaDescEditor } from './PresetConfig'

const SIDE_A_COLOR = '#2196f3'
const SIDE_B_COLOR = '#ff9800'

const AggregateStats = ({ clips }) => {
  const completed = clips.filter((c) => c.resultA && c.resultB)
  if (completed.length === 0) return null

  const avgLatencyA =
    completed.reduce((s, c) => s + c.resultA.latency_ms, 0) / completed.length
  const avgLatencyB =
    completed.reduce((s, c) => s + c.resultB.latency_ms, 0) / completed.length
  const avgTokensA =
    completed.reduce(
      (s, c) =>
        s + (c.resultA.prompt_tokens || 0) + (c.resultA.completion_tokens || 0),
      0
    ) / completed.length
  const avgTokensB =
    completed.reduce(
      (s, c) =>
        s + (c.resultB.prompt_tokens || 0) + (c.resultB.completion_tokens || 0),
      0
    ) / completed.length
  const avgThinkingA =
    completed.reduce((s, c) => s + (c.resultA.thinking_tokens || 0), 0) /
    completed.length
  const avgThinkingB =
    completed.reduce((s, c) => s + (c.resultB.thinking_tokens || 0), 0) /
    completed.length

  let momentDiffCount = 0
  let titleDiffCount = 0
  completed.forEach((c) => {
    const aTopMoment = c.resultA.moments?.[0]
    const bTopMoment = c.resultB.moments?.[0]
    if (aTopMoment && bTopMoment) {
      if (
        Math.abs(aTopMoment.start_s - bTopMoment.start_s) > 1 ||
        Math.abs(aTopMoment.end_s - bTopMoment.end_s) > 1
      ) {
        momentDiffCount++
      }
    }
    const aTopTitle = c.resultA.titles?.[0]
    const bTopTitle = c.resultB.titles?.[0]
    if (aTopTitle !== bTopTitle) titleDiffCount++
  })

  const stat = (label, value) => (
    <Box
      sx={{
        flex: 1,
        backgroundColor: 'background.paper',
        borderRadius: '8px',
        p: 1.5,
        textAlign: 'center',
        minWidth: 120,
      }}
    >
      <Box sx={{ fontSize: '1.1em', fontWeight: 700 }}>{value}</Box>
      <Box sx={{ fontSize: '0.75em', color: 'text.secondary', mt: 0.5 }}>
        {label}
      </Box>
    </Box>
  )

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ fontWeight: 700, fontSize: '0.9em', mb: 1 }}>
        Aggregate Stats ({completed.length} clips)
      </Box>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        {stat('Avg Latency A', `${(avgLatencyA / 1000).toFixed(1)}s`)}
        {stat('Avg Latency B', `${(avgLatencyB / 1000).toFixed(1)}s`)}
        {stat('Avg Tokens A', Math.round(avgTokensA).toLocaleString())}
        {stat('Avg Tokens B', Math.round(avgTokensB).toLocaleString())}
        {stat('Avg Thinking A', Math.round(avgThinkingA).toLocaleString())}
        {stat('Avg Thinking B', Math.round(avgThinkingB).toLocaleString())}
        {stat('#1 Moment Differs', `${momentDiffCount}/${completed.length}`)}
        {stat('Top Title Differs', `${titleDiffCount}/${completed.length}`)}
      </Box>
    </Box>
  )
}

const ClipCard = ({ clip }) => {
  const [expanded, setExpanded] = useState(false)
  const [audioBlobUrl, setAudioBlobUrl] = useState(null)
  const [audioLoading, setAudioLoading] = useState(false)
  const [playingKey, setPlayingKey] = useState(null)
  const [titlesExpanded, setTitlesExpanded] = useState(false)
  const [expandedMoments, setExpandedMoments] = useState(new Set())
  const [transcriptExpanded, setTranscriptExpanded] = useState(false)
  const audioRef = useRef(null)
  const endTimeRef = useRef(null)

  const toggleTitles = useCallback(() => setTitlesExpanded((v) => !v), [])
  const toggleMoment = useCallback(
    (rank) =>
      setExpandedMoments((prev) => {
        const next = new Set(prev)
        if (next.has(rank)) next.delete(rank)
        else next.add(rank)
        return next
      }),
    []
  )
  const toggleTranscript = useCallback(
    () => setTranscriptExpanded((v) => !v),
    []
  )

  const isDone = clip.resultA && clip.resultB
  const hasError = clip.error

  const aTopMoment = clip.resultA?.moments?.[0]
  const bTopMoment = clip.resultB?.moments?.[0]
  const momentsDiffer =
    aTopMoment &&
    bTopMoment &&
    (Math.abs(aTopMoment.start_s - bTopMoment.start_s) > 1 ||
      Math.abs(aTopMoment.end_s - bTopMoment.end_s) > 1)

  useEffect(() => {
    if (!expanded || audioBlobUrl || audioLoading) return
    setAudioLoading(true)
    api.reclips
      .getReclipAudio(clip.sourceId, false)
      .then((blob) => setAudioBlobUrl(URL.createObjectURL(blob)))
      .catch(() => {})
      .finally(() => setAudioLoading(false))
  }, [expanded, audioBlobUrl, audioLoading, clip.sourceId])

  useEffect(() => {
    return () => {
      if (audioBlobUrl) URL.revokeObjectURL(audioBlobUrl)
    }
  }, [audioBlobUrl])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return
    const onTimeUpdate = () => {
      if (
        endTimeRef.current != null &&
        audio.currentTime >= endTimeRef.current
      ) {
        audio.pause()
        endTimeRef.current = null
        setPlayingKey(null)
      }
    }
    audio.addEventListener('timeupdate', onTimeUpdate)
    return () => audio.removeEventListener('timeupdate', onTimeUpdate)
  }, [audioBlobUrl])

  const stopAudio = useCallback(() => {
    endTimeRef.current = null
    if (audioRef.current) audioRef.current.pause()
    setPlayingKey(null)
  }, [])

  const playMoment = useCallback((moment, side) => {
    const audio = audioRef.current
    if (!audio) return
    audio.pause()
    endTimeRef.current = moment.end_s
    audio.currentTime = moment.start_s
    const p = audio.play()
    if (p) p.catch(() => {})
    setPlayingKey(`${side}-${moment.rank}`)
  }, [])

  const handleToggle = useCallback(() => {
    if (!isDone) return
    if (expanded) stopAudio()
    setExpanded(!expanded)
  }, [isDone, expanded, stopAudio])

  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        borderRadius: '8px',
        mb: 1.5,
        overflow: 'hidden',
        border: momentsDiffer ? '1px solid' : '1px solid transparent',
        borderColor: momentsDiffer ? 'warning.main' : 'transparent',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 1.5,
          cursor: isDone ? 'pointer' : 'default',
        }}
        onClick={handleToggle}
      >
        <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
          {hasError ? (
            <ErrorOutlineIcon sx={{ fontSize: 18, color: 'error.main' }} />
          ) : isDone ? (
            <CheckCircleOutlineIcon
              sx={{ fontSize: 18, color: 'success.main' }}
            />
          ) : (
            <CircularProgress size={16} />
          )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              fontSize: '0.82em',
              fontFamily: 'monospace',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {clip.sourceId}
          </Box>
          {isDone && (
            <Box sx={{ fontSize: '0.75em', color: 'text.secondary', mt: 0.3 }}>
              {clip.resultA.word_count} words &middot; {clip.resultA.duration_s}
              s &middot; A: {(clip.resultA.latency_ms / 1000).toFixed(1)}s / B:{' '}
              {(clip.resultB.latency_ms / 1000).toFixed(1)}s
              {aTopMoment && bTopMoment && (
                <Box component="span" sx={{ ml: 1 }}>
                  &middot; A: {aTopMoment.start_s.toFixed(1)}&ndash;
                  {aTopMoment.end_s.toFixed(1)}s (
                  {(aTopMoment.end_s - aTopMoment.start_s).toFixed(1)}s)
                  {' / '}B: {bTopMoment.start_s.toFixed(1)}&ndash;
                  {bTopMoment.end_s.toFixed(1)}s (
                  {(bTopMoment.end_s - bTopMoment.start_s).toFixed(1)}s)
                </Box>
              )}
              {momentsDiffer && (
                <Box
                  component="span"
                  sx={{ ml: 1, color: 'warning.main', fontWeight: 700 }}
                >
                  ⚡ differs
                </Box>
              )}
            </Box>
          )}
          {hasError && (
            <Box sx={{ fontSize: '0.75em', color: 'error.main', mt: 0.3 }}>
              {clip.error}
            </Box>
          )}
        </Box>

        {isDone && (
          <IconButton size="small">
            {expanded ? (
              <ExpandLessIcon sx={{ fontSize: 18 }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        )}
      </Box>

      <Collapse in={expanded}>
        {isDone && (
          <Box sx={{ px: 1.5, pb: 1.5 }}>
            <Divider sx={{ mb: 1.5 }} />
            {audioBlobUrl && (
              <Box sx={{ mb: 1.5 }}>
                <audio
                  ref={audioRef}
                  src={audioBlobUrl}
                  preload="auto"
                  style={{ width: '100%', height: 28 }}
                  controls
                />
              </Box>
            )}
            {audioLoading && (
              <Box sx={{ fontSize: '0.75em', color: 'text.secondary', mb: 1 }}>
                Loading audio...
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <ExpandedSide
                label="A"
                result={clip.resultA}
                accentColor={SIDE_A_COLOR}
                playingKey={playingKey}
                onPlay={(m) => playMoment(m, 'A')}
                onStop={stopAudio}
                side="A"
                hasAudio={!!audioBlobUrl}
                titlesExpanded={titlesExpanded}
                onToggleTitles={toggleTitles}
                expandedMoments={expandedMoments}
                onToggleMoment={toggleMoment}
                transcriptExpanded={transcriptExpanded}
                onToggleTranscript={toggleTranscript}
                sourceId={clip.sourceId}
              />
              <Divider orientation="vertical" flexItem />
              <ExpandedSide
                label="B"
                result={clip.resultB}
                accentColor={SIDE_B_COLOR}
                playingKey={playingKey}
                onPlay={(m) => playMoment(m, 'B')}
                onStop={stopAudio}
                side="B"
                hasAudio={!!audioBlobUrl}
                titlesExpanded={titlesExpanded}
                onToggleTitles={toggleTitles}
                expandedMoments={expandedMoments}
                onToggleMoment={toggleMoment}
                transcriptExpanded={transcriptExpanded}
                onToggleTranscript={toggleTranscript}
                sourceId={clip.sourceId}
              />
            </Box>
          </Box>
        )}
      </Collapse>
    </Box>
  )
}

const ExpandedSide = ({
  label,
  result,
  accentColor,
  playingKey,
  onPlay,
  onStop,
  side,
  hasAudio,
  titlesExpanded,
  onToggleTitles,
  expandedMoments,
  onToggleMoment,
  transcriptExpanded,
  onToggleTranscript,
  sourceId,
}) => (
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: accentColor,
          mr: 0.5,
        }}
      />
      <Box sx={{ fontWeight: 700, fontSize: '0.8em' }}>Prompt {label}</Box>
      <CopyIconButton
        getText={() =>
          formatVerboseResultExport(sourceId, result, `Prompt ${label}`)
        }
        title="Copy verbose diagnostics"
      />
    </Box>

    <Box sx={{ fontSize: '0.75em', color: 'text.secondary', mb: 1 }}>
      {result.model_used} &middot; Tokens:{' '}
      {result.prompt_tokens?.toLocaleString()} prompt &middot;{' '}
      {result.completion_tokens?.toLocaleString()} completion &middot;{' '}
      {result.thinking_tokens?.toLocaleString()}/
      {result.thinking_budget?.toLocaleString()} thinking
    </Box>

    {result.auto_emojis?.length > 0 && (
      <Box
        sx={{
          fontSize: '1.2em',
          letterSpacing: '0.2em',
          mb: 0.5,
          pl: 1,
          borderLeft: '2px solid',
          borderColor: accentColor,
        }}
      >
        {result.auto_emojis.join(' ')}
      </Box>
    )}

    {result.best_quote && (
      <Box
        sx={{
          fontSize: '0.78em',
          fontStyle: 'italic',
          mb: 1,
          pl: 1,
          borderLeft: '2px solid',
          borderColor: accentColor,
        }}
      >
        &ldquo;{result.best_quote}&rdquo;
      </Box>
    )}

    {result.animation_context && (
      <Box
        sx={{
          fontSize: '0.78em',
          mb: 1,
          pl: 1,
          borderLeft: '2px dashed',
          borderColor: accentColor,
        }}
      >
        {result.animation_context}
      </Box>
    )}

    {result.titles?.length > 0 && (
      <Box sx={{ mb: 1 }}>
        <ExpandableTitles
          titles={result.titles}
          fontSize="0.78em"
          expanded={titlesExpanded}
          onToggle={onToggleTitles}
        />
      </Box>
    )}

    {result.moments?.map((m) => {
      const key = `${side}-${m.rank}`
      const isPlaying = playingKey === key
      return (
        <Box
          key={m.rank}
          sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}
        >
          {hasAudio && (
            <IconButton
              size="small"
              onClick={() => (isPlaying ? onStop() : onPlay(m))}
              sx={{ p: 0.25, mr: 0.5, mt: '-1px' }}
            >
              {isPlaying ? (
                <StopIcon sx={{ fontSize: 14 }} />
              ) : (
                <PlayArrowIcon sx={{ fontSize: 14 }} />
              )}
            </IconButton>
          )}
          <Box
            sx={{
              fontSize: '0.75em',
              color: m.rank === 1 ? 'text.primary' : 'text.secondary',
            }}
          >
            #{m.rank} [{m.start_s.toFixed(1)}s&ndash;{m.end_s.toFixed(1)}s] (
            {(m.end_s - m.start_s).toFixed(1)}s){' '}
            <ExpandableText
              text={m.transcript_slice}
              maxLength={60}
              fontSize="0.75em"
              expanded={expandedMoments?.has(m.rank)}
              onToggle={() => onToggleMoment?.(m.rank)}
            />
            {expandedMoments?.has(m.rank) && m.why && (
              <Box
                sx={{
                  mt: 0.5,
                  fontStyle: 'italic',
                  color: 'text.secondary',
                  fontSize: '0.95em',
                }}
              >
                {m.why}
              </Box>
            )}
          </Box>
        </Box>
      )
    })}

    <Box sx={{ mt: 1 }}>
      <ExpandableTranscript
        transcript={result.punctuated_text}
        label="full transcript"
        fontSize="0.72em"
        expanded={transcriptExpanded}
        onToggle={onToggleTranscript}
      />
    </Box>
  </Box>
)

const AutoTrimBatch = () => {
  const [presets, setPresets] = useState([])
  const sideA = usePresetSide(presets)
  const sideB = usePresetSide(presets)
  const [modelA, setModelA] = useState('gemini-2.5-flash-lite')
  const [modelB, setModelB] = useState('gemini-2.5-flash-lite')
  const [thinkingConfigA, setThinkingConfigA] = useState({})
  const [thinkingConfigB, setThinkingConfigB] = useState({})
  const [numCandidatesA, setNumCandidatesA] = useState(3)
  const [numCandidatesB, setNumCandidatesB] = useState(3)
  const [count, setCount] = useState('10')
  const [myClipsOnly, setMyClipsOnly] = useState(false)
  const [captureMode, setCaptureMode] = useState('all')
  const [isRunning, setIsRunning] = useState(false)
  const [clips, setClips] = useState([])
  const [error, setError] = useState(null)
  const abortRef = useRef(false)

  useEffect(() => {
    return () => {
      abortRef.current = true
    }
  }, [])

  useEffect(() => {
    api.autoTrim
      .getPrompt()
      .then((data) => {
        setPresets(data.presets || [])
      })
      .catch((err) => setError(err.message || 'Failed to load presets'))
  }, [])

  const runBatchForIds = useCallback(
    async (sourceIds) => {
      setIsRunning(true)
      setError(null)
      abortRef.current = false

      const initialClips = sourceIds.map((id) => ({
        sourceId: id,
        resultA: null,
        resultB: null,
        error: null,
      }))
      setClips(initialClips)

      const paramsA = sideA.getApiParams()
      const paramsB = sideB.getApiParams()

      const CONCURRENCY = 2
      for (let i = 0; i < sourceIds.length; i += CONCURRENCY) {
        if (abortRef.current) break
        const batch = sourceIds.slice(i, i + CONCURRENCY)
        await Promise.all(
          batch.map(async (sid, batchIdx) => {
            const idx = i + batchIdx
            if (abortRef.current) return
            try {
              const [a, b] = await Promise.all([
                api.autoTrim.testAutoTrim(sid, {
                  ...paramsA,
                  model: modelA,
                  numCandidates: numCandidatesA,
                  ...thinkingConfigA,
                }),
                api.autoTrim.testAutoTrim(sid, {
                  ...paramsB,
                  model: modelB,
                  numCandidates: numCandidatesB,
                  ...thinkingConfigB,
                }),
              ])
              if (abortRef.current) return
              setClips((prev) => {
                const next = [...prev]
                next[idx] = { ...next[idx], resultA: a, resultB: b }
                return next
              })
            } catch (err) {
              setClips((prev) => {
                const next = [...prev]
                next[idx] = { ...next[idx], error: err.message || 'Failed' }
                return next
              })
            }
          })
        )
      }
      setIsRunning(false)
    },
    [
      sideA,
      sideB,
      modelA,
      modelB,
      thinkingConfigA,
      thinkingConfigB,
      numCandidatesA,
      numCandidatesB,
    ]
  )

  const handleRun = useCallback(async () => {
    try {
      const sourceIds = await api.reclips.getRandomSourceIds(
        parseInt(count) || 10,
        15,
        7,
        myClipsOnly,
        captureMode === 'all' ? null : captureMode
      )
      await runBatchForIds(sourceIds)
    } catch (err) {
      setError(err.message || 'Failed to fetch random source IDs')
      setIsRunning(false)
    }
  }, [count, myClipsOnly, captureMode, runBatchForIds])

  const handleRerun = useCallback(async () => {
    const sourceIds = clips.map((c) => c.sourceId)
    if (sourceIds.length === 0) return
    await runBatchForIds(sourceIds)
  }, [clips, runBatchForIds])

  const handleStop = useCallback(() => {
    abortRef.current = true
  }, [])

  const handleClear = useCallback(() => {
    setClips([])
    setError(null)
    abortRef.current = true
  }, [])

  const completedCount = clips.filter(
    (c) => c.resultA || c.resultB || c.error
  ).length

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        justifyContent: 'center',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Left panel */}
      <Box sx={{ width: 500, height: '100%', overflow: 'auto', flexShrink: 0 }}>
        <Box sx={{ fontSize: '1.2em', fontWeight: 700, mb: 1 }}>
          Batch Compare
        </Box>
        <Text>
          Compare two prompt versions across multiple random clips. Fetches N
          random eligible reclips and runs both prompts on each in parallel.
        </Text>

        {/* Side A */}
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: SIDE_A_COLOR,
              mr: 1,
            }}
          />
          <Header
            text={sideA.promptModified ? 'Prompt A (Modified)' : 'Prompt A'}
            mt={0}
          />
          {sideA.promptModified && (
            <Button
              size="small"
              onClick={sideA.resetPrompt}
              sx={{ ml: 2, fontSize: '0.7em', textTransform: 'none' }}
            >
              Reset
            </Button>
          )}
        </Box>
        <PresetSelector
          presets={presets}
          presetKey={sideA.presetKey}
          onPresetChange={sideA.selectPreset}
          minS={sideA.minS}
          maxS={sideA.maxS}
          onMinChange={sideA.setMinS}
          onMaxChange={sideA.setMaxS}
          compact
        />
        <TextArea value={sideA.prompt} onChange={sideA.setPrompt} rows={5} />
        <SchemaDescEditor
          schemaDescs={sideA.schemaDescs}
          originalSchemaDescs={sideA.originalSchemaDescs}
          onSchemaDescChange={sideA.setSchemaDesc}
          onResetSchema={sideA.resetSchema}
          compact
        />
        <ModelAndThinkingConfig
          model={modelA}
          onModelChange={setModelA}
          onChange={setThinkingConfigA}
          numCandidates={numCandidatesA}
          onNumCandidatesChange={setNumCandidatesA}
          compact
        />

        {/* Side B */}
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: SIDE_B_COLOR,
              mr: 1,
            }}
          />
          <Header
            text={sideB.promptModified ? 'Prompt B (Modified)' : 'Prompt B'}
            mt={0}
          />
          {sideB.promptModified && (
            <Button
              size="small"
              onClick={sideB.resetPrompt}
              sx={{ ml: 2, fontSize: '0.7em', textTransform: 'none' }}
            >
              Reset
            </Button>
          )}
        </Box>
        <PresetSelector
          presets={presets}
          presetKey={sideB.presetKey}
          onPresetChange={sideB.selectPreset}
          minS={sideB.minS}
          maxS={sideB.maxS}
          onMinChange={sideB.setMinS}
          onMaxChange={sideB.setMaxS}
          compact
        />
        <TextArea value={sideB.prompt} onChange={sideB.setPrompt} rows={5} />
        <SchemaDescEditor
          schemaDescs={sideB.schemaDescs}
          originalSchemaDescs={sideB.originalSchemaDescs}
          onSchemaDescChange={sideB.setSchemaDesc}
          onResetSchema={sideB.resetSchema}
          compact
        />
        <ModelAndThinkingConfig
          model={modelB}
          onModelChange={setModelB}
          onChange={setThinkingConfigB}
          numCandidates={numCandidatesB}
          onNumCandidatesChange={setNumCandidatesB}
          compact
        />

        <Header text="Clip Count" mt={2} />
        <Box
          sx={{
            '& input': {
              backgroundColor: 'background.paper',
              width: 80,
              display: 'block',
              border: 0,
              p: 1.5,
              borderWidth: 1,
              borderStyle: 'solid',
              borderColor: 'divider',
              borderRadius: '8px',
              fontSize: '0.95em',
              '&:focus': { borderColor: 'white', outline: 'none' },
            },
          }}
        >
          <input
            type="number"
            min={1}
            max={50}
            value={count}
            onChange={(e) => setCount(e.target.value)}
            onBlur={() => {
              const n = parseInt(count)
              setCount(String(Math.max(1, Math.min(50, n || 10))))
            }}
          />
        </Box>

        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={myClipsOnly}
              onChange={(e) => setMyClipsOnly(e.target.checked)}
              disabled={isRunning}
            />
          }
          label="My clips only"
          sx={{
            mt: 1.5,
            ml: 0,
            '& .MuiFormControlLabel-label': { fontSize: '0.85em' },
          }}
        />

        <Header text="Capture Mode" mt={2} />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={captureMode}
          onChange={(e, v) => v != null && setCaptureMode(v)}
          disabled={isRunning}
        >
          <ToggleButton value="all" sx={{ textTransform: 'none' }}>
            All
          </ToggleButton>
          <ToggleButton value="auto" sx={{ textTransform: 'none' }}>
            Auto only
          </ToggleButton>
          <ToggleButton value="manual" sx={{ textTransform: 'none' }}>
            Manual only
          </ToggleButton>
        </ToggleButtonGroup>

        <Box
          sx={{
            mt: 2,
            mb: 4,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Button
            variant="outlined"
            onClick={handleClear}
            disabled={clips.length === 0 && !error}
          >
            Clear
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isRunning && (
              <Box sx={{ fontSize: '0.82em', color: 'text.secondary' }}>
                {completedCount}/{clips.length}
              </Box>
            )}
            {!isRunning && clips.length > 0 && (
              <Button variant="outlined" onClick={handleRerun}>
                Re-run Same Clips
              </Button>
            )}
            {isRunning ? (
              <Button variant="outlined" color="warning" onClick={handleStop}>
                Stop
              </Button>
            ) : (
              <LoadingButton
                variant="contained"
                onClick={handleRun}
                loading={isRunning}
              >
                Run Batch
              </LoadingButton>
            )}
          </Box>
        </Box>

        {error && (
          <Box
            sx={{
              backgroundColor: 'background.paper',
              p: 2,
              borderRadius: '8px',
              color: 'error.main',
              whiteSpace: 'pre-wrap',
              fontSize: '0.9em',
            }}
          >
            {error}
          </Box>
        )}
      </Box>

      <Divider orientation="vertical" sx={{ mx: 3 }} />

      {/* Right panel */}
      <Box sx={{ flex: 1, height: '100%', overflow: 'auto', minWidth: 0 }}>
        {clips.length === 0 && !isRunning && (
          <Box sx={{ color: 'text.secondary', fontSize: '0.9em', mt: 4 }}>
            Results will appear here after running a batch.
          </Box>
        )}

        {clips.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <CopyResultsButton getText={() => formatBatchExport(clips)} />
          </Box>
        )}
        {clips.length > 0 && <AggregateStats clips={clips} />}

        {clips.map((clip, i) => (
          <ClipCard key={clip.sourceId || i} clip={clip} />
        ))}
      </Box>
    </Box>
  )
}

export default AutoTrimBatch
